import { Avatar, Button, Col, RecordingIndicator, Text, useTheme } from "@rally/ui-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { coalesced } from "../chat/activity-refresh.ts";
import { useAttaching } from "../chat/attachment/attaching.ts";
import { Composer, type SubmissionIdentity } from "../chat/composer/composer.tsx";
import { draftContextFor, keepDraftContext } from "../chat/composer/draft.ts";
import type { Candidate } from "../chat/composer/mentions.ts";
import { sameReplyTarget } from "../chat/composer/submission.ts";
import { Marked } from "../chat/message/marked.tsx";
import { canonicalTypedPayload } from "../chat/message/message-payload.ts";
import type { Card } from "../chat/panel/stacking.ts";
import type { Reaction } from "../chat/reaction/reacting.ts";
import { useLive } from "../chat/run/live.ts";
import { TargetActions } from "../chat/target-actions.tsx";
import { failureSaid } from "../localization/failure.ts";
import { formatDateTime, formatTime } from "../localization/format.ts";
import { say } from "../localization/say.ts";
import { calledBy } from "../me/called.ts";
import type { PutAway, TargetCapabilities } from "../net/api.ts";
import { act, read } from "../net/rally.ts";
import { hears } from "../sound/sounding.ts";
import { type CallMetadata, type PersonInCall, useCalling } from "./calling.ts";
import { ranFor } from "./line.tsx";
import { useCallMedia } from "./media-session.tsx";
import { CallRecording, type CallRecordingRead } from "./recording.tsx";
import { type CallReply, callReplyBelongs, callReplyIn } from "./reply-context.ts";
import { CallRoom } from "./room.tsx";
import { CallStrip } from "./strip.tsx";

export type CallTranscriptLine = {
  id: string;
  by: string;
  text: string;
  at: string;
  revised: string | null;
};

export type CallDiscussionLine = {
  id: string;
  by: string;
  text: string;
  at: string;
  reactions: Reaction[];
  away: PutAway;
  answering: { kind: "call" | "message"; id: string; said: string };
  capabilities: TargetCapabilities;
};

export type CallResourceRead = {
  call: CallMetadata & {
    spontaneous: boolean;
    scheduledAt: string | null;
    host: { account: string; handle: string; displayName: string | null } | null;
  };
  participants: PersonInCall[];
  transcript: CallTranscriptLine[];
  discussion: CallDiscussionLine[];
  reactions: Reaction[];
  attention: PutAway;
  capabilities: TargetCapabilities;
  durableMedia: CallRecordingRead[];
};

export function callCard(call: string, conversation: string, me: string): Card {
  return {
    key: `call:${call}`,
    title: say("call.resource"),
    render: () => <CallResource call={call} conversation={conversation} me={me} />,
  };
}

function personName(person: PersonInCall): string {
  return calledBy(person) ?? person.handle;
}

function People({ people }: { people: readonly PersonInCall[] }) {
  const theme = useTheme();
  return (
    <Col style={{ gap: theme.space[2] }} testID="call-resource-participants">
      <Text typeName="caption" color="text.tertiary">
        {say("call.participants")}
      </Text>
      <Col style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.space[2] }}>
        {people.map((person) => (
          <Col
            key={person.account}
            style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}
          >
            <Avatar name={personName(person)} size={24} />
            <Text typeName="caption" color="text.secondary">
              {personName(person)}
            </Text>
          </Col>
        ))}
      </Col>
    </Col>
  );
}

function Transcript({
  lines,
  people,
}: {
  lines: readonly CallTranscriptLine[];
  people: readonly PersonInCall[];
}) {
  const theme = useTheme();
  const byAccount = new Map(people.map((person) => [person.account, person]));
  return (
    <Col style={{ gap: theme.space[2] }} testID="call-resource-transcript">
      <Text typeName="caption" color="text.tertiary">
        {say("call.transcript")}
      </Text>
      {lines.length === 0 ? (
        <Text typeName="caption" color="text.tertiary">
          {say("call.noTranscript")}
        </Text>
      ) : (
        lines.map((line) => {
          const person = byAccount.get(line.by);
          return (
            <Col key={line.id} style={{ gap: 2 }}>
              <Text typeName="label" color="text.primary">
                {person ? personName(person) : line.by.slice(0, 8)}
              </Text>
              <Marked text={line.text} who={[]} />
            </Col>
          );
        })
      )}
    </Col>
  );
}

function Discussion({
  lines,
  people,
  again,
  onReply,
}: {
  lines: readonly CallDiscussionLine[];
  people: readonly PersonInCall[];
  again: () => void;
  onReply: (line: CallDiscussionLine) => void;
}) {
  const theme = useTheme();
  const byAccount = new Map(people.map((person) => [person.account, person]));
  return (
    <Col style={{ gap: theme.space[3] }} testID="call-resource-discussion">
      <Text typeName="caption" color="text.tertiary">
        {say("call.discussion")}
      </Text>
      {lines.map((line) => {
        const person = byAccount.get(line.by);
        return (
          <Col key={line.id} style={{ gap: theme.space[1] }}>
            <Text typeName="label" color="text.primary">
              {person ? personName(person) : line.by.slice(0, 8)}
            </Text>
            {line.answering.kind === "message" ? (
              <Text typeName="caption" color="text.tertiary" numberOfLines={1}>
                {`“${line.answering.said}”`}
              </Text>
            ) : null}
            <Marked text={line.text} who={[]} />
            <TargetActions
              target={{ kind: "message", id: line.id }}
              reactions={line.reactions}
              away={line.away}
              capabilities={line.capabilities}
              onReply={() => onReply(line)}
              onChanged={again}
              testID={`call-discussion-actions-${line.id}`}
            />
          </Col>
        );
      })}
    </Col>
  );
}

export function CallResource({ call, conversation, me }: { call: string; conversation: string; me: string }) {
  const theme = useTheme();
  const [resource, setResource] = useState<CallResourceRead | null>(null);
  const calling = useCalling(conversation, null);
  const joined = calling.joined && calling.live?.call === call;
  const media = useCallMedia();
  const attaching = useAttaching(conversation);
  const replyShelf = `${me}:call:${call}:reply-context`;
  const [replying, setReplying] = useState<CallReply | null>(() => callReplyIn(draftContextFor(replyShelf)));
  const chooseReply = useCallback(
    (next: CallReply | null) => {
      setReplying(next);
      keepDraftContext(replyShelf, next);
    },
    [replyShelf],
  );
  const [focusComposer, setFocusComposer] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const captured = useRef<string | null | undefined>(undefined);
  const again = useCallback(() => {
    void read<CallResourceRead>("call.resource", { call })
      .then((said) => {
        setResource(said);
        const status = said.durableMedia[0]?.status ?? null;
        const before = captured.current;
        captured.current = status;
        if (before !== null || (status !== "starting" && status !== "active")) return;
        hears({ what: "recording began", conversation: said.call.conversation, call });
      })
      .catch(() => setResource(null));
  }, [call]);
  useEffect(again, [again]);
  useEffect(() => {
    if (resource?.call.state !== "live") return;
    const timer = setInterval(again, 4000);
    return () => clearInterval(timer);
  }, [again, resource?.call.state]);
  const refresh = useMemo(() => coalesced(again), [again]);
  useEffect(() => () => refresh.cancel(), [refresh]);
  useLive((notice) => {
    if (notice.conversation === conversation) refresh.notice();
  });
  const activeReply =
    replying && resource && callReplyBelongs(replying, call, resource.discussion) ? replying : null;
  const latestReply = useRef<CallReply | null>(activeReply);
  latestReply.current = activeReply;
  useEffect(() => {
    if (resource && replying && !activeReply) chooseReply(null);
  }, [activeReply, chooseReply, replying, resource]);
  const post = async (text: string, identify: SubmissionIdentity): Promise<boolean> => {
    const sentReply = activeReply;
    const carried = await attaching.ready();
    if (!text && carried.length === 0) return false;
    const parent = sentReply ?? { kind: "call" as const, id: call, said: say("call.resource") };
    try {
      const replyTo = { kind: parent.kind, id: parent.id };
      const files = carried.map(({ key, name, mediaType }) => ({ key, name, mediaType }));
      const submission = identify(canonicalTypedPayload({ conversation, text, replyTo, files }));
      await act("message.post", {
        conversation,
        text,
        submission,
        replyTo,
        ...(files.length > 0 ? { files } : {}),
      });
      attaching.taken(carried);
      if (sameReplyTarget(sentReply, latestReply.current)) chooseReply(null);
      setError(null);
      again();
      return true;
    } catch (cause) {
      attaching.retry(carried);
      setError(failureSaid(cause, say("chat.broke")));
      return false;
    }
  };

  if (!resource) {
    return (
      <Text typeName="caption" color="text.tertiary" testID="call-resource-loading">
        {say("head.opening")}
      </Text>
    );
  }

  const live = resource.call.state === "live";
  const recording = resource.durableMedia[0] ?? null;
  const candidates: Candidate[] = resource.participants.map((person) => ({
    account: person.account,
    handle: person.handle,
    displayName: person.displayName,
    avatar: person.avatar,
    kind: "person",
    here: true,
  }));
  const times = live
    ? say("call.liveSince")(resource.call.startedAt ? formatTime(resource.call.startedAt) : "")
    : say("call.replayTimes")(
        resource.call.startedAt ? formatDateTime(resource.call.startedAt) : "",
        resource.call.endedAt ? formatDateTime(resource.call.endedAt) : "",
        ranFor(resource.call.startedAt, resource.call.endedAt),
      );

  return (
    <Col style={{ gap: theme.space[4] }} testID={`call-resource-${call}`}>
      <Text typeName="caption" color="text.tertiary" testID="call-resource-times">
        {times}
      </Text>

      <TargetActions
        target={{ kind: "call", id: call }}
        reactions={resource.reactions}
        away={resource.attention}
        capabilities={resource.capabilities}
        onReply={() => {
          chooseReply({ kind: "call", id: call, said: say("call.resource") });
          setFocusComposer((was) => was + 1);
        }}
        onChanged={again}
        testID="call-resource-actions"
      />

      {live ? (
        <Col style={{ gap: theme.space[3] }} testID="call-resource-live">
          {recording ? (
            <Col testID="call-recording-indicator" style={{ paddingHorizontal: theme.space[4] }}>
              <RecordingIndicator
                recording={
                  recording.status === "starting" ||
                  recording.status === "active" ||
                  recording.status === "ending"
                }
                duration={
                  recording.status === "starting" ? say("recording.preparing") : say("recording.active")
                }
              />
            </Col>
          ) : null}
          <CallStrip
            startedAt={resource.call.startedAt}
            here={resource.participants}
            me={me}
            joined={joined}
            onJoin={() => void calling.join()}
          />
          {joined ? (
            <CallRoom
              speakers={media.speakers}
              here={resource.participants}
              me={me}
              wired={media.wired}
              broke={media.broke}
              mic={media.mic}
              camera={media.camera}
              onMic={media.toggleMic}
              onCamera={media.toggleCamera}
              onLeave={() => void calling.leave()}
            />
          ) : (
            <Button size="sm" variant="soft" onPress={() => void calling.join()} testID="call-resource-join">
              {say("call.join")}
            </Button>
          )}
          {recording ? <CallRecording recording={recording} live /> : null}
        </Col>
      ) : (
        <Col style={{ gap: theme.space[3] }} testID="call-resource-replay">
          <People people={resource.participants} />
          {recording ? (
            <CallRecording recording={recording} live={false} />
          ) : (
            <Text typeName="caption" color="text.tertiary" testID="call-resource-no-media">
              {say("call.noDurableMedia")}
            </Text>
          )}
        </Col>
      )}

      <Transcript lines={resource.transcript} people={resource.participants} />
      <Discussion
        lines={resource.discussion}
        people={resource.participants}
        again={again}
        onReply={(line) => {
          chooseReply({ kind: "message", id: line.id, said: line.text });
          setFocusComposer((was) => was + 1);
        }}
      />
      <Composer
        conversation={conversation}
        draftKey={`call:${call}:${activeReply?.kind ?? "call"}:${activeReply?.id ?? call}`}
        placeholder={say("call.reply")}
        testID="call-composer"
        who={candidates}
        replyContext={activeReply ? { said: activeReply.said, onClose: () => chooseReply(null) } : undefined}
        error={error}
        focusSignal={focusComposer}
        picked={attaching.picked}
        onPick={attaching.take}
        onDrop={attaching.drop}
        onSay={(text, _from, identify) => post(text, identify)}
      />
    </Col>
  );
}
